import { useCallback, useMemo } from "react";
import { Store, TundraStorefrontSummary } from "../../../../../common/models";
import {
    useGetAllStorefrontsQuery,
    useImportThemesMutation,
} from "../../../datastore/TundraStorefrontDataStore";
import { TundraStorefrontMoveCopyThemeResult } from "../../../../../common/models/TundraStorefront";
import {
    FormConfig,
    GenericFormReturn,
    useGenericForm
} from "../../../common/hooks/useGenericForm";
import { parseThemeNumbers } from "../utils/themeUtils";
import { MISSING_REQUIRED_INFORMATION } from "../StoreValidationHelpers";
import {
    validateExistingStorefrontId,
    validateThemes,
    validateThemeImportForm,
} from "../utils/themeValidation";

export interface ThemeImportFormData {
    existingStorefrontId: string;
    themes: string;
}

export interface StoreThemesImportValues extends GenericFormReturn<ThemeImportFormData> {
    storefrontsData: TundraStorefrontSummary[] | null;
    storefrontsLoading: boolean;
    storefrontsError: any;
    submitImportThemes: () => Promise<void>;
    importThemesLoading: boolean;
    importThemesError: any;
    importThemesCalled: boolean;
}

export const useThemeImporter = (store: Store): StoreThemesImportValues => {
    // Use the getAllStorefronts query
    const {
        data: storefrontsData,
        loading: storefrontsLoading,
        error: storefrontsError,
    } = useGetAllStorefrontsQuery();

    // Update store mutation
    const [
        importThemes,
        {
            loading: importThemesLoading,
            error: importThemesError,
            called: importThemesCalled,
            data: importThemesData,
        },
    ] = useImportThemesMutation();

    const submitOperationConfig = useMemo(
        () => {
            let successMessage = "Imported all themes successfully.";
            let successDescription = "";
            if (
                importThemesData &&
                importThemesData.results.some((result) => !result.success)
            ) {
                const failedImports = importThemesData.results
                    .filter((result: TundraStorefrontMoveCopyThemeResult) => !result.success)
                    .map((result: TundraStorefrontMoveCopyThemeResult) => result.srcVersion);

                const successfulImports = importThemesData.results
                    .filter((result: TundraStorefrontMoveCopyThemeResult) => result.success)
                    .map((result: TundraStorefrontMoveCopyThemeResult) => result.srcVersion);

                successMessage =
                    `Successfully imported theme(s): ${successfulImports.join(", ")}.`;
                successDescription =
                    failedImports.length > 0
                        ? `Failed to import theme(s): ${failedImports.join(", ")}`
                        : "";
            }

            return {
                loading: importThemesLoading,
                called: importThemesCalled,
                error: importThemesError,
                successMessage: successMessage,
                successDescription: successDescription,
                failureMessage: "Failed to import themes.",
                onSuccess: () => { },
            }
        },
        [importThemesLoading, importThemesCalled, importThemesError]
    );

    // Form configuration for theme import
    const formConfig: FormConfig<ThemeImportFormData> = useMemo(
        () => ({
            submitOperationConfig,
            fields: [
                {
                    name: "existingStorefrontId",
                    required: false,
                    validate: (value: string, formValues: ThemeImportFormData | undefined) => {
                        const themes = formValues?.themes ?? "";
                        return validateExistingStorefrontId(value, themes);
                    },
                },
                {
                    name: "themes",
                    required: false,
                    validate: (value: string, formValues: ThemeImportFormData | undefined) => {
                        const existingStorefrontId = formValues?.existingStorefrontId ?? "";
                        return validateThemes(value, existingStorefrontId);
                    },
                },
            ],
            validateForm: (formValues: ThemeImportFormData) => {
                const { existingStorefrontId, themes } = formValues;
                return validateThemeImportForm(existingStorefrontId, themes);
            },
        }),
        [submitOperationConfig]
    );

    const initialValues: ThemeImportFormData = useMemo(() => ({
        existingStorefrontId: "",
        themes: "",
    }), []);

    // Use the generic form hook for form state management
    const themesForm = useGenericForm<ThemeImportFormData>({
        initialValues,
        formConfig,
        initialFormStatusMessage: MISSING_REQUIRED_INFORMATION,
    });

    const submitImportThemes = useCallback(async (): Promise<void> => {
        // Validate form before submission
        if (!themesForm.isFormValid) {
            return;
        }

        const { existingStorefrontId, themes } = themesForm.formValues;

        // Filter out empty strings and non-numeric values from theme numbers input field
        const themeNumbers = parseThemeNumbers(themes);

        await importThemes({
            destDomain: store.domain ?? "",
            srcDomain: existingStorefrontId.trim(),
            srcVersion: themes.trim() !== "" ? themeNumbers : [],
            type: "COPY",
        });
    }, [
        importThemes,
        store.domain,
        themesForm.formValues,
        themesForm.isFormValid,
    ]);

    const value: StoreThemesImportValues = {
        ...themesForm,
        storefrontsData,
        storefrontsError,
        storefrontsLoading,
        submitImportThemes,
        importThemesLoading,
        importThemesError,
        importThemesCalled,
    };

    return value;
};
